import {create} from "zustand";
import {createJSONStorage, persist} from "zustand/middleware";
import {RegionTreeVO, useQueryRegionTree} from "@/service/region.s";
import {get} from "lodash";

enum RegionEnum {
    Regions = "regions",
}

type RegionStore = {
    regions: RegionType[];
    actions: {
        setRegions: (regions: RegionType[]) => void;
        clearRegion: () => void;
    };
};

export type RegionType = {
    group: string;
    title: string;
    value: string;
};

export type AncestorType = {
    [key in string]: { title: string, value: string }
}

const useRegionStore = create<RegionStore>()(
    persist(
        (set) => ({
            regions: [],
            actions: {
                setRegions: (regions: RegionType[]) => set({regions}),
                clearRegion: () => set({regions: []}),
            },
        }),
        {
            name: "regionStore",
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                [RegionEnum.Regions]: state.regions,
            }),
        },
    ),
);

export const useGetRegionTree = () => {
    const {setRegions} = useRegionActions();
    const {mutateAsync: queryRegionTreeMutate} = useQueryRegionTree();

    const flattenRegion = (trees: RegionTreeVO[]): RegionType[] => {
        const areas: RegionType[] = [];
        const cities: RegionType[] = [];
        const provinces: RegionType[] = [];
        const traverse = (tree: RegionTreeVO, ancestor = {} as AncestorType) => {
            if (!tree) return;
            if (tree.children) {
                if (tree.level == 1) {
                    ancestor["province"] = {title: tree.title, value: tree.value};
                    provinces.push({
                        title: tree.title,
                        group: "省/直辖市",
                        value: `${tree.value}`,
                    })
                } else if (tree.level == 2) {
                    ancestor["city"] = {title: tree.title, value: tree.value};
                    const province = get(ancestor, "province", {title: "", value: 0});
                    cities.push({
                        title: tree.title,
                        group: province.title,
                        value: `${province.value},${tree.value}`,
                    });
                }
                tree.children.forEach((child) => traverse(child, ancestor));
            } else {
                const city = get(ancestor, "city", {title: "", value: 0});
                const province = get(ancestor, "province", {title: "", value: 0});
                areas.push({
                    title: tree.title,
                    group: city.title,
                    value: `${province.value},${city?.value},${tree.value}`,
                });
            }
        };
        trees?.forEach((tree: RegionTreeVO) => traverse(tree));

        const result = provinces.concat(cities).concat(areas);
        return result;
    };

    const regionTree = async () => {
        const {data} = await queryRegionTreeMutate();
        setRegions(flattenRegion(data!));
    };

    return regionTree;
};

export const useRegions = () => useRegionStore((state) => state.regions);

export const useRegionActions = () => useRegionStore((state) => state.actions);
